import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import cn from 'classnames';
import { Button, Callout, Typography } from 'seagull';
import { useIoContext } from '~/contexts';
import { Intent } from '~/infra/access-control';
import styles from './ConnectCreditCard.module.scss';
import mpmStyles from './ManagePaymentMethods.module.scss';
import { useConnectCreditCard } from './useConnectCreditCard';
const Label = (props)=>{
    const { children, size = 'small' } = props;
    return /*#__PURE__*/ _jsx(Typography, {
        className: cn(mpmStyles.label, {
            [mpmStyles.mediumSize]: size === 'medium'
        }),
        variant: size === 'small' ? 'paragraph2' : 'paragraph1',
        children: children
    });
};
const Field = (props)=>/*#__PURE__*/ _jsx("div", {
        className: cn(mpmStyles.field, {
            [mpmStyles.marginTop]: !props.isFirst,
            [mpmStyles.largeMargin]: props.largeMargin
        }, props.className),
        children: props.children
    });
const ErrorText = (props)=>/*#__PURE__*/ _jsx(Typography, _object_spread_props(_object_spread({}, props), {
        className: cn(mpmStyles.error, props.className),
        variant: "paragraph2"
    }));
const BaseInput = /*#__PURE__*/ forwardRef(function Input(props, ref) {
    const { className, complete, invalid } = props, rest = _object_without_properties(props, [
        "className",
        "complete",
        "invalid"
    ]);
    return /*#__PURE__*/ _jsx("input", _object_spread_props(_object_spread({}, rest), {
        className: cn(mpmStyles.input, {
            [mpmStyles.complete]: complete,
            [mpmStyles.invalid]: invalid
        }, className),
        ref: ref
    }));
});
const Input = (props)=>{
    const { valueState } = props, rest = _object_without_properties(props, [
        "valueState"
    ]);
    return /*#__PURE__*/ _jsx(BaseInput, _object_spread_props(_object_spread({}, rest), {
        complete: valueState.touched && !valueState.error,
        invalid: valueState.touched && !!valueState.error,
        value: valueState.value
    }));
};
/**
 * Legacy credit card form UI.
 * Expects to be wrapped in an Elements provider from @stripe/react-stripe-js.
 */ export default function ConnectCreditCardLegacy(props) {
    const { reactStripe: { CardNumberElement, CardExpiryElement, CardCvcElement } } = useIoContext();
    const { cardNum, cardNumDispatch, expiry, expiryDispatch, cvv, cvvDispatch, nameOnCard, setNameOnCard, zipCode, setZipCode, apiError, setApiError, submitting, allFieldsValid, disabledReason, doPaymentMethodAction, stripeLoadingFailed, styleObj, classesObj, validateNameOnCard, validateZipCode, connectCreditCard } = useConnectCreditCard(props);
    return /*#__PURE__*/ _jsxs("form", {
        onSubmit: (e)=>{
            e.preventDefault();
            doPaymentMethodAction(connectCreditCard);
        },
        children: [
            /*#__PURE__*/ _jsxs(Field, {
                isFirst: true,
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        children: "Name on card"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        valueState: nameOnCard,
                        placeholder: "Enter your name",
                        "data-testid": "manage-payment-methods-credit-card-name",
                        className: cn(mpmStyles.input, {
                            [mpmStyles.invalid]: nameOnCard.touched && nameOnCard.error
                        }),
                        disabled: !!disabledReason,
                        onChange: (ev)=>{
                            setApiError(undefined);
                            const error = validateNameOnCard(ev.currentTarget.value);
                            setNameOnCard(_object_spread_props(_object_spread({}, nameOnCard), {
                                value: ev.currentTarget.value,
                                pristine: false,
                                error
                            }));
                        },
                        onBlur: ()=>{
                            const error = validateNameOnCard(nameOnCard.value);
                            setNameOnCard(_object_spread_props(_object_spread({}, nameOnCard), {
                                error,
                                touched: true
                            }));
                        }
                    }),
                    nameOnCard.error && nameOnCard.touched && /*#__PURE__*/ _jsx(ErrorText, {
                        "data-testid": "connect-cc-name-error",
                        children: nameOnCard.error
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Field, {
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        children: "Card number"
                    }),
                    /*#__PURE__*/ _jsx(CardNumberElement, {
                        className: cn(mpmStyles.input, {
                            [mpmStyles.invalid]: cardNum.touched && !cardNum.valid && cardNum.errorMessage
                        }),
                        options: {
                            disabled: !!disabledReason,
                            style: styleObj,
                            classes: classesObj,
                            placeholder: '0000-0000-0000-0000'
                        },
                        onChange: (event)=>{
                            const { complete, error, empty } = event;
                            setApiError(undefined);
                            if (complete) {
                                cardNumDispatch({
                                    type: 'valid'
                                });
                            } else {
                                cardNumDispatch({
                                    type: 'error',
                                    errorMessage: error ? error.message : empty ? 'Please enter a valid card number' : ''
                                });
                            }
                        },
                        onBlur: ()=>cardNumDispatch({
                                type: 'blur'
                            })
                    }),
                    cardNum.touched && !cardNum.valid && /*#__PURE__*/ _jsx(ErrorText, {
                        "data-testid": "connect-cc-num-error",
                        children: cardNum.errorMessage
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.ccExpirationRow,
                children: [
                    /*#__PURE__*/ _jsxs(Field, {
                        className: styles.ccExpirationField,
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "Expiration date"
                            }),
                            /*#__PURE__*/ _jsx(CardExpiryElement, {
                                "data-testid": "manage-payment-methods-credit-card-expiry",
                                className: cn(mpmStyles.input, {
                                    [mpmStyles.invalid]: expiry.touched && expiry.errorMessage
                                }),
                                options: {
                                    disabled: !!disabledReason,
                                    style: styleObj,
                                    classes: classesObj,
                                    placeholder: 'MM/YY'
                                },
                                onChange: (event)=>{
                                    const { complete, error, empty } = event;
                                    setApiError(undefined);
                                    if (complete) {
                                        expiryDispatch({
                                            type: 'valid'
                                        });
                                    } else {
                                        expiryDispatch({
                                            type: 'error',
                                            errorMessage: error ? error.message : empty ? 'Please enter a valid card expiry date' : ''
                                        });
                                    }
                                },
                                onBlur: ()=>expiryDispatch({
                                        type: 'blur'
                                    })
                            }),
                            expiry.touched && !expiry.valid && /*#__PURE__*/ _jsx(ErrorText, {
                                "data-testid": "connect-cc-expiry-error",
                                children: expiry.errorMessage
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Field, {
                        className: styles.ccExpirationField,
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "CVV"
                            }),
                            /*#__PURE__*/ _jsx(CardCvcElement, {
                                className: cn(mpmStyles.input, {
                                    [mpmStyles.invalid]: cvv.touched && cvv.errorMessage
                                }),
                                options: {
                                    disabled: !!disabledReason,
                                    style: styleObj,
                                    classes: classesObj,
                                    placeholder: 'CVV'
                                },
                                onChange: (event)=>{
                                    const { complete, error, empty } = event;
                                    setApiError(undefined);
                                    if (complete) {
                                        cvvDispatch({
                                            type: 'valid'
                                        });
                                    } else {
                                        cvvDispatch({
                                            type: 'error',
                                            errorMessage: error ? error.message : empty ? 'Please enter a valid CVV' : ''
                                        });
                                    }
                                },
                                onBlur: ()=>cvvDispatch({
                                        type: 'blur'
                                    })
                            }),
                            cvv.touched && !cvv.valid && /*#__PURE__*/ _jsx(ErrorText, {
                                "data-testid": "connect-cc-cvv-error",
                                children: cvv.errorMessage
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Field, {
                        className: styles.ccExpirationField,
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "Zip code"
                            }),
                            /*#__PURE__*/ _jsx(Input, {
                                valueState: zipCode,
                                type: "text",
                                placeholder: "Zip code",
                                "data-testid": "connect-cc-zip",
                                disabled: !!disabledReason,
                                className: cn(mpmStyles.input, {
                                    [mpmStyles.invalid]: zipCode.touched && zipCode.error
                                }),
                                onChange: (ev)=>{
                                    setApiError(undefined);
                                    const value = ev.target.value.trim();
                                    const error = validateZipCode(value, -1);
                                    setZipCode(_object_spread_props(_object_spread({}, zipCode), {
                                        value,
                                        pristine: false,
                                        error
                                    }));
                                },
                                onBlur: ()=>{
                                    const error = validateZipCode(zipCode.value, -1);
                                    setZipCode(_object_spread_props(_object_spread({}, zipCode), {
                                        touched: true,
                                        error
                                    }));
                                }
                            }),
                            zipCode.error && zipCode.touched && /*#__PURE__*/ _jsx(ErrorText, {
                                "data-testid": "connect-cc-zip-error",
                                children: zipCode.error
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Field, {
                largeMargin: true,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        type: "submit",
                        size: "small",
                        disabled: !!(!allFieldsValid || apiError || stripeLoadingFailed),
                        loading: submitting,
                        testId: "manage-payment-methods-add-credit-card",
                        intent: Intent.AddPaymentMethod,
                        bi: {
                            name: 'connect_credit_card'
                        },
                        tooltip: {
                            placement: 'top',
                            title: disabledReason ? /*#__PURE__*/ _jsx("div", {
                                "data-testid": "add-cc-disabled-reason",
                                children: disabledReason
                            }) : '',
                            testId: 'connect-cc-submit-btn-tooltip',
                            disableFocusListener: disabledReason == null,
                            disableHoverListener: disabledReason == null,
                            disableTouchListener: disabledReason == null
                        },
                        children: "Connect credit card"
                    }),
                    (apiError || stripeLoadingFailed) && /*#__PURE__*/ _jsx(Callout, {
                        className: styles.error,
                        intent: "error",
                        testId: "manage-payment-methods-cc-error",
                        children: stripeLoadingFailed ? 'An error has occurred. Refresh the page and try again' : apiError
                    })
                ]
            })
        ]
    });
}
